import { useEffect, useRef, useState } from "react";

import LanguageSwitcher from "@/components/shared/LanguageSwitcher";
import Logo from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";

export default function Hero({
  onStart,
  onSignIn,
  onLocaleChange,
  signedInNavItems = [],
  countryCode = "",
  browserLanguages = [],
  shareLinkLanguageCode = "",
  prototypeEnabled = false,
  onPrototypeAwaken,
}) {
  const { locale, t, tx } = useI18n();
  const videoRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(min-width: 60rem)").matches
  ));
  const [showPoster, setShowPoster] = useState(() => (
    typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && (window.matchMedia("(prefers-reduced-motion: reduce)").matches || navigator.connection?.saveData === true)
  ));
  const hasSignedInNavItems = Array.isArray(signedInNavItems) && signedInNavItems.length > 0;
  const hasReturningAccount = signedInNavItems.some((item) => item.key === "configure");
  const isLocalized = locale !== "en";
  const heroHeadline = `${t("landing.hero_line_1")} ${t("landing.hero_line_2")}`;
  const mobileHeroWords = heroHeadline.split(/\s+/);

  useEffect(() => {
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const desktopQuery = window.matchMedia("(min-width: 60rem)");
    const syncViewport = () => setIsDesktop(desktopQuery.matches);
    const syncPlayback = () => {
      const shouldShowPoster = motionQuery.matches || navigator.connection?.saveData === true;
      setShowPoster(shouldShowPoster);
      if (shouldShowPoster) {
        videoRef.current?.pause();
      }
    };
    syncPlayback();
    syncViewport();
    motionQuery.addEventListener("change", syncPlayback);
    desktopQuery.addEventListener("change", syncViewport);
    return () => {
      motionQuery.removeEventListener("change", syncPlayback);
      desktopQuery.removeEventListener("change", syncViewport);
    };
  }, []);

  useEffect(() => {
    if (showPoster) return;
    void videoRef.current?.play().catch(() => setShowPoster(true));
  }, [isDesktop, showPoster]);

  return (
    <header
      className={`landing-hero landing-hero--live${isLocalized ? " landing-hero--localized" : ""}${prototypeEnabled ? " landing-hero--prototype" : ""}`}
      data-testid="landing-hero"
    >
      <nav className="landing-nav">
        <a href="#top" className="landing-nav__brand">
          <Logo size="md" />
        </a>
        <div className={`landing-nav__actions${hasReturningAccount ? " landing-nav__actions--returning" : ""}`}>
          <a className="landing-nav__pricing" href="#pricing">
            {tx("Pricing")}
          </a>
          <LanguageSwitcher
            className="landing-nav__language"
            countryCode={countryCode}
            browserLanguages={browserLanguages}
            shareLinkLanguageCode={shareLinkLanguageCode}
            onChange={(nextLanguageCode) => onLocaleChange?.(nextLanguageCode, "switcher")}
          />
          {hasSignedInNavItems ? (
            <div className="landing-nav__signed-in">
              {signedInNavItems.map((item) => (
                <Button
                  key={item.key}
                  type="button"
                  variant={item.key === "preferences" ? "outline" : "ghost"}
                  size="sm"
                  className={item.key === "configure" ? "landing-nav__account-return" : undefined}
                  onClick={item.onClick}
                >
                  {tx(item.label)}
                </Button>
              ))}
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="landing-nav__sign-in"
              onClick={onSignIn}
            >
              {t("landing.sign_in_up")}
            </Button>
          )}
        </div>
      </nav>

      <div className="landing-hero__media">
        <img
          className="landing-hero__desktop-background"
          src="/landing/hero-scene-desktop.png"
          alt=""
          width="2896"
          height="1396"
        />
        {showPoster ? (
          <img
            className="landing-hero__video landing-hero__video--poster"
            src={isDesktop ? "/landing/hero-scene-desktop.png" : "/landing/hero-poster.jpg"}
            alt=""
            width={isDesktop ? "2896" : "1920"}
            height={isDesktop ? "1396" : "1080"}
          />
        ) : (
          <video
            key={isDesktop ? "desktop" : "mobile"}
            ref={videoRef}
            className="landing-hero__video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={isDesktop ? "/landing/hero-scene-desktop.png" : "/landing/hero-poster.jpg"}
            aria-hidden="true"
            data-testid="landing-hero-video"
            onError={() => setShowPoster(true)}
          >
            <source src={isDesktop ? "/landing/hero-desktop.mp4" : "/landing/hero.mp4"} type="video/mp4" />
          </video>
        )}
        <div className="landing-hero__wash" aria-hidden="true" />
        <p className="sr-only">
          {tx("A person listens to a voice note while getting ready in the kitchen.")}
        </p>
        <div className="landing-hero__content">
          <h1 aria-label={heroHeadline}>
            {isLocalized ? (
              <>
                <span>{t("landing.hero_line_1")}</span>{" "}
                <span>{t("landing.hero_line_2")}</span>
              </>
            ) : (
              <>
                <span className="landing-hero__headline-desktop" aria-hidden="true">{t("landing.hero_line_1")}</span>
                <span className="landing-hero__headline-desktop" aria-hidden="true">{t("landing.hero_line_2")}</span>
                <span className="landing-hero__headline-mobile" aria-hidden="true">{mobileHeroWords.slice(0, 2).join(" ")}</span>
                <span className="landing-hero__headline-mobile" aria-hidden="true">{mobileHeroWords.slice(2, 5).join(" ")}</span>
                <span className="landing-hero__headline-mobile" aria-hidden="true">{mobileHeroWords.slice(5).join(" ")}</span>
              </>
            )}
          </h1>
          <p>{t("landing.subtitle")}</p>
          <div className="landing-hero__cta-row">
            <Button
              type="button"
              className="landing-button landing-button--primary"
              data-testid={prototypeEnabled ? "living-qr-awaken" : undefined}
              onClick={() => (prototypeEnabled ? onPrototypeAwaken?.() : onStart?.("fun"))}
            >
              {t("landing.cta")}
            </Button>
            <a className="landing-button landing-button--outline" href="#pricing">
              {tx("Pricing")}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
